/**
 * Plain words for "everything the hub sent" (G11, UV2).
 *
 * Pure: no DOM. One chip word and one sentence per item, plus the Home counts
 * line. Hold reasons reuse V1's HOLD_WORDING so the card and this list cannot
 * drift into two sets of sentences. No code, id, refId or snake_case token is
 * ever part of a sentence; those belong behind Show details.
 */

import {
  HOLD_REASON_CODES,
  type HoldReasonCode,
} from "../../../ingest-worker/src/hold_reasons.js";
import type {
  AdminHubIntakeCounts,
  AdminHubIntakeItem,
  HubIntakeReason,
  HubIntakeState,
  StaffRole,
} from "../types.js";
import { moneyText, moreText, type AccountLabelContext } from "./display-words.js";
import { account as accountFace } from "./face.js";
import { HOLD_WORDING } from "./hold-wording.js";
import { decimalToCents, formatCents } from "./money.js";
import { navLabel } from "./sentences.js";

/**
 * The page names this module uses, built from the registry (B2b-7): a sentence
 * cannot drift from the nav label, and a renamed page renames here too.
 */
const TO_REVIEW = navLabel("queue");
const BOOKS = navLabel("general-ledger");
/** The one label for the row's "answer it on To review" control. */
const OPEN_IN_REVIEW = `Open in ${TO_REVIEW}`;

/** V1's HOLD_WORDING is keyed by the same 30 hold codes WV2 writes. */
const HOLD_CODES: ReadonlySet<string> = new Set(HOLD_REASON_CODES);

export function isHubIntakeHoldReason(
  reason: HubIntakeReason,
): reason is HoldReasonCode {
  return HOLD_CODES.has(reason);
}

export type HubIntakeNonHoldReason = Exclude<HubIntakeReason, HoldReasonCode>;

/** Whole-section sentences. Constants, so no dynamic text reaches the screen. */
export const HUB_INTAKE_NOT_SET_UP =
  "This company is not set up to receive receipts and bills from the hub.";
export const HUB_INTAKE_LEDGER_OFF =
  "The saved books copy is not available, so booked items cannot be shown right now.";

/** Chip words, keyed over the mirror union so a missing state fails typecheck. */
export const HUB_INTAKE_STATE_WORDS: Readonly<Record<HubIntakeState, string>> = {
  booked: "Booked",
  waiting_on_you: "Needs you",
  with_the_owner: "With the owner",
  with_the_practice: "With the hub",
  held: "On hold",
  not_booked_yet: "Not booked yet",
  on_the_shelf: "No bank line",
  waiting_for_the_bank_line: "Waiting for the bank",
  paid_by_another_company: "Paid by another company",
  set_aside: "Set aside",
};

/**
 * Sentences for the non-hold reasons. Hold keys come from HOLD_WORDING; this
 * Record is over the mirror's Exclude union, so a new non-hold reason without
 * words fails typecheck rather than rendering an empty line.
 */
export const HUB_INTAKE_REASON_WORDS: Readonly<
  Record<HubIntakeNonHoldReason, string>
> = {
  wallet_card: "A wallet card for this bill needs a look.",
  wallet_auto_booking_off:
    "Automatic booking is off for wallet bills, so this waits for your OK.",
  transfer_needs_you:
    "The hub confirmed this transfer, but it does not book itself yet. Pick the account.",
  other: `This needs a look in ${TO_REVIEW}.`,
  not_arrived_yet: "The bank line has not come in yet.",
  no_paid_from_account:
    "The hub did not say which account paid this bill, so Books cannot find the bank line.",
  bank_line_not_found: "No bank line matched this bill within 10 days.",
  wallet_payment_not_seen:
    "The wallet payment for this bill has not shown up yet.",
  bank_line_removed: "The bank removed the line this was matched to.",
  several_bank_lines_match:
    "More than one bank line could be this transfer, so none was used.",
  no_bank_line_after_4_days:
    "No bank line matched this receipt after 4 days. It is in the list below.",
  answered_waiting_for_update: "You answered this. It books on the next update.",
  waiting_for_next_update: "This books on the next update.",
  books_copy_not_available:
    "This month's books are not loaded yet, so we cannot show it as booked.",
  check_in_books: `${BOOKS} has more than one entry for this. Open ${BOOKS} and search for it.`,
  paid_by_another_company:
    "This was paid with another of your companies' cards, so it shows in that company's books.",
  not_ours: "You marked this as not ours.",
  duplicate: "You marked this as a duplicate.",
  card_dismissed: "The card for this was dismissed.",
};

function roleIsOwner(role: StaffRole): boolean {
  return role === "owner";
}

function holdSentence(code: HoldReasonCode, role: StaffRole): string {
  const wording = HOLD_WORDING[code];
  return `${wording.happened} ${roleIsOwner(role) ? wording.ownerNext : wording.amNext}`;
}

/**
 * The most accounts the Worker names for one booking (`MAX_BOOKING_ACCOUNTS`
 * in ingest-worker/src/hub_intake.ts; a test keeps the two equal). A booking
 * that comes back with this many may have had more.
 */
export const BOOKING_ACCOUNTS_LIMIT = 20;

/** Shown for an amount that cannot be read. Never an empty cell. */
export const HUB_INTAKE_UNREADABLE_AMOUNT = "\u2014";

/** The transfer kinds the contract allows (`AdminHubIntakeItem.transferKind`). */
export type HubTransferKind = NonNullable<AdminHubIntakeItem["transferKind"]>;

/** Which way the money moved, when the stored data says. */
export type HubMoneyDirection = "out" | "in" | "unknown";

/**
 * A transfer's direction, from its kind (rd L2 review round 1, HIGH).
 *
 * The hub sends a transfer as an unsigned magnitude and the kind names the
 * direction (`books-api.yaml` `AdminHubIntakeItem.amount` only covers purchases
 * and receipts; `ingest-worker/src/hub_intake.ts` `transferDraft` passes the
 * stored amount through unchanged). `loan_to` and `owner_distribution` are money
 * out, `loan_from` and `capital_contribution` are money in — see
 * `pipeline/ai_categorizer.py` `character_agrees_with_proposal`, which requires
 * outflow for the first two and inflow for the last two.
 *
 * Two kinds do not name a side: an `intercompany_transfer` is Due-From on the
 * sender and Due-To on the receiver from the same record, and a
 * `loan_repayment` settles a claim either way (paying one down is money out,
 * collecting one is money in). The hub item carries no side for either, so they
 * are shown unsigned rather than guessed. The Record is over the contract's
 * closed set, so a seventh kind fails typecheck instead of rendering blank.
 */
const TRANSFER_DIRECTION: Readonly<Record<HubTransferKind, HubMoneyDirection>> = {
  loan_to: "out",
  owner_distribution: "out",
  loan_from: "in",
  capital_contribution: "in",
  intercompany_transfer: "unknown",
  loan_repayment: "unknown",
};

/**
 * A hub amount in the sign the rest of the console uses (rd L2). The hub's
 * own sign is money-out-positive ("A purchase or payment is positive; a
 * receipt refund is negative", `books-api.yaml` `AdminHubIntakeItem.amount`),
 * while Activity, Books and To review are money-in-positive. One list must not
 * show bills positive and receipts negative, so the hub's sign is flipped
 * once, here. A transfer is the exception: its amount is a magnitude and its
 * kind carries the direction.
 */
export function hubIntakeDisplayCents(item: {
  amount: string;
  kind: AdminHubIntakeItem["kind"];
  transferKind?: HubTransferKind;
}): bigint | null {
  const cents = decimalToCents(item.amount);
  if (cents === null) return null;
  if (item.kind !== "transfer") return -cents;
  const magnitude = cents < 0n ? -cents : cents;
  if (item.transferKind === undefined) return magnitude;
  const direction = TRANSFER_DIRECTION[item.transferKind];
  return direction === "out" ? -magnitude : magnitude;
}

/** The row's money, in that one sign, or a dash when unreadable. */
export function hubIntakeAmountText(
  item: Pick<AdminHubIntakeItem, "amount" | "kind" | "transferKind">,
): string {
  const cents = hubIntakeDisplayCents(item);
  return cents === null ? HUB_INTAKE_UNREADABLE_AMOUNT : formatCents(cents);
}

export function bookingAccountsCapped(received: number): boolean {
  return received >= BOOKING_ACCOUNTS_LIMIT;
}

function bookedSentence(
  item: AdminHubIntakeItem,
  companies: AccountLabelContext,
): string {
  const raw = item.booking?.accounts ?? [];
  // The one account face (B2b-7): `accountDisplayName` is a helper B5 retires.
  const accounts = raw.map((account) => accountFace(account, companies));
  const first = accounts[0];
  if (first === undefined) return "Booked.";
  if (accounts.length === 1) return `Booked to ${first}.`;
  // The Worker sends at most BOOKING_ACCOUNTS_LIMIT accounts per booking: at
  // that length the real number is not known, so the sentence stops counting.
  return `Booked to ${first} and ${moreText(accounts.length - 1, !bookingAccountsCapped(raw.length))}.`;
}

export function hubIntakeChip(item: AdminHubIntakeItem, role: StaffRole): string {
  if (item.state === "with_the_owner" && roleIsOwner(role)) return "Needs you";
  return HUB_INTAKE_STATE_WORDS[item.state];
}

/**
 * The one sentence a row shows. A hold reason speaks through V1's happened
 * and the signed-in role's next step; every other state has one fixed line.
 */
export function hubIntakeSentence(
  item: AdminHubIntakeItem,
  role: StaffRole,
  companies: AccountLabelContext,
): string {
  if (item.state === "booked") return bookedSentence(item, companies);

  if (item.state === "with_the_practice") {
    return "This is waiting for an answer from the hub.";
  }
  if (item.state === "held") {
    return HUB_INTAKE_REASON_WORDS.wallet_auto_booking_off;
  }
  if (item.state === "with_the_owner" && !roleIsOwner(role)) {
    return "You sent this to the owner.";
  }

  const reason = item.reason;
  if (reason !== undefined) {
    if (isHubIntakeHoldReason(reason)) {
      if (item.state === "waiting_on_you" || item.state === "with_the_owner") {
        return holdSentence(reason, role);
      }
    } else if (reason === "check_in_books") {
      // The next step, with the amount the row already shows (rd Y3): "look in
      // Books" is a dead end without it. A malformed amount keeps the plain
      // wording rather than printing a raw string.
      const amount = moneyText(item.amount);
      if (amount === "") return HUB_INTAKE_REASON_WORDS.check_in_books;
      return `${BOOKS} has more than one entry for this. Open ${BOOKS} and search for ${amount}.`;
    } else {
      return HUB_INTAKE_REASON_WORDS[reason];
    }
  }

  if (item.state === "not_booked_yet") {
    return HUB_INTAKE_REASON_WORDS.waiting_for_next_update;
  }
  if (item.state === "on_the_shelf") {
    return HUB_INTAKE_REASON_WORDS.no_bank_line_after_4_days;
  }
  if (item.state === "waiting_for_the_bank_line") {
    return HUB_INTAKE_REASON_WORDS.not_arrived_yet;
  }
  if (item.state === "paid_by_another_company") {
    return HUB_INTAKE_REASON_WORDS.paid_by_another_company;
  }
  if (item.state === "set_aside") {
    return HUB_INTAKE_REASON_WORDS.not_ours;
  }
  return HUB_INTAKE_REASON_WORDS.other;
}

/**
 * The label is a `string`, not a literal type: it is now built from the nav
 * registry (`navLabel`), so a page rename reaches every row. The consumer
 * (`pages/receipt-shelf.ts`, group b4) renders it as text.
 */
export type HubIntakeAction =
  | { kind: "books"; label: string; period: string }
  | { kind: "review"; label: string }
  | { kind: "shelf"; label: string };

/** At most one action per row; the caller renders exactly this or nothing. */
export function hubIntakeAction(
  item: AdminHubIntakeItem,
  role: StaffRole,
): HubIntakeAction | null {
  if (item.state === "booked") {
    return booksAction(item);
  }
  if (
    item.state === "not_booked_yet" &&
    item.reason === "check_in_books"
  ) {
    return booksAction(item);
  }
  if (item.state === "waiting_on_you") {
    return { kind: "review", label: OPEN_IN_REVIEW };
  }
  if (item.state === "with_the_owner") {
    return roleIsOwner(role) ? { kind: "review", label: OPEN_IN_REVIEW } : null;
  }
  if (item.state === "held") {
    return { kind: "review", label: OPEN_IN_REVIEW };
  }
  if (item.state === "on_the_shelf") {
    return { kind: "shelf", label: "See the list below" };
  }
  return null;
}

function booksAction(item: AdminHubIntakeItem): HubIntakeAction | null {
  // "See in Books" opens the entry's month. It is shown only for a row that
  // has a booking (rd polish rules §1): a bank reference id never goes into a
  // visible search box, so the action carries the period only.
  if (item.booking === undefined) return null;
  const line = item.bankLine;
  if (line === undefined) return null;
  const period = line.date.slice(0, 7);
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(period)) return null;
  return {
    kind: "books",
    label: "See in Books",
    period,
  };
}

export type HubIntakeFilterId =
  | "all"
  | "booked"
  | "needs_you"
  | "with_owner"
  | "waiting"
  | "set_aside";

/**
 * One count as it can honestly be shown: a finite non-negative whole number, or
 * null when the response did not carry one. A partial counts map used to render
 * "NaN" in every chip (rd Y3); a missing count is not a zero and not a number.
 */
function countValue(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? Math.trunc(value)
    : null;
}

/** True when all ten counts arrived as numbers. */
export function hubIntakeCountsArrived(
  counts: AdminHubIntakeCounts,
): boolean {
  const row = counts as unknown as Readonly<Record<string, unknown>>;
  return [
    "booked",
    "waiting_on_you",
    "with_the_owner",
    "with_the_practice",
    "held",
    "not_booked_yet",
    "on_the_shelf",
    "waiting_for_the_bank_line",
    "paid_by_another_company",
    "set_aside",
  ].every((key) => countValue(row[key]) !== null);
}

export interface HubIntakeFilter {
  id: HubIntakeFilterId;
  label: string;
  /** Null when the number would mislead (Booked while the books copy is off). */
  count: number | null;
  states: readonly HubIntakeState[] | null;
}

/** Which states a chip covers. Null means "all". */
export function hubIntakeFilterStates(
  id: HubIntakeFilterId,
  role: StaffRole,
): readonly HubIntakeState[] | null {
  const owner = roleIsOwner(role);
  if (id === "all") return null;
  if (id === "booked") return ["booked"];
  if (id === "needs_you") {
    return owner
      ? ["waiting_on_you", "held", "with_the_owner"]
      : ["waiting_on_you", "held"];
  }
  if (id === "waiting") {
    return [
      "waiting_for_the_bank_line",
      "on_the_shelf",
      "not_booked_yet",
      "with_the_practice",
    ];
  }
  if (id === "with_owner") return ["with_the_owner"];
  return ["set_aside", "paid_by_another_company"];
}

/**
 * The status chips, their counts and the states each one covers. While the
 * saved books copy is off, nothing can be shown as booked, so the Booked chip
 * carries no number: "Booked 0" would read as "nothing booked". When the counts
 * may be short (`countsComplete` false) no chip prints an exact number either;
 * the page says some items are not counted.
 *
 * One grouping with Home (rd polish rules §1, R7-21): the account manager's
 * Waiting excludes items with the owner, and those get their own chip. Home's
 * line already separates them, so the two screens cannot give two totals for
 * the same counts.
 */
export function hubIntakeFilters(
  counts: AdminHubIntakeCounts,
  role: StaffRole,
  ledgerAvailable: boolean,
  countsComplete = true,
): readonly HubIntakeFilter[] {
  const owner = roleIsOwner(role);
  const n = (key: keyof AdminHubIntakeCounts): number =>
    countValue(counts[key]) ?? 0;
  const arrived = hubIntakeCountsArrived(counts);
  const shown = (value: number): number | null =>
    arrived && countsComplete ? value : null;
  const needsYou =
    n("waiting_on_you") + n("held") + (owner ? n("with_the_owner") : 0);
  const waiting =
    n("waiting_for_the_bank_line") +
    n("on_the_shelf") +
    n("not_booked_yet") +
    n("with_the_practice");
  const waitingStates: readonly HubIntakeState[] = [
    "waiting_for_the_bank_line",
    "on_the_shelf",
    "not_booked_yet",
    "with_the_practice",
  ];
  const chips: HubIntakeFilter[] = [
    {
      id: "all",
      label: "All",
      count: shown(hubIntakeCountsTotal(counts)),
      states: null,
    },
    {
      id: "booked",
      label: "Booked",
      count: ledgerAvailable ? shown(n("booked")) : null,
      states: ["booked"],
    },
    {
      id: "needs_you",
      label: "Needs you",
      count: shown(needsYou),
      states: owner
        ? ["waiting_on_you", "held", "with_the_owner"]
        : ["waiting_on_you", "held"],
    },
  ];
  if (!owner) {
    chips.push({
      id: "with_owner",
      label: "With the owner",
      count: shown(n("with_the_owner")),
      states: ["with_the_owner"],
    });
  }
  chips.push(
    {
      id: "waiting",
      label: "Waiting",
      count: shown(waiting),
      states: waitingStates,
    },
    {
      id: "set_aside",
      label: "Set aside",
      count: shown(n("set_aside") + n("paid_by_another_company")),
      states: ["set_aside", "paid_by_another_company"],
    },
  );
  return chips;
}

/** Every state the hub sent this month, whatever Home or a chip shows. */
export function hubIntakeCountsTotal(counts: AdminHubIntakeCounts): number {
  const row = counts as unknown as Readonly<Record<string, unknown>>;
  let total = 0;
  for (const value of Object.values(row)) {
    total += countValue(value) ?? 0;
  }
  return total;
}

/**
 * Home's one line. Only non-zero parts, in the order the owner reads them.
 *
 * "Nothing has come in" is said only when EVERY count (all ten states) is zero
 * and the counts are complete. Items that were set aside, or paid by another
 * company, did come in, so they are named when no shown part carries them.
 * When the counts may be short (a scan limit was hit) the line never reads as
 * an exact total and never says nothing came in.
 */
export function homeCountsLine(
  counts: AdminHubIntakeCounts,
  role: StaffRole,
  countsComplete: boolean,
): string {
  const owner = roleIsOwner(role);
  const n = (key: keyof AdminHubIntakeCounts): number =>
    countValue(counts[key]) ?? 0;
  const needYou =
    n("waiting_on_you") + n("held") + (owner ? n("with_the_owner") : 0);
  const waiting =
    n("waiting_for_the_bank_line") +
    n("on_the_shelf") +
    n("not_booked_yet") +
    n("with_the_practice");
  const parts: string[] = [];
  if (n("booked") > 0) parts.push(`${String(n("booked"))} booked`);
  if (needYou > 0) parts.push(`${String(needYou)} need you`);
  if (!owner && n("with_the_owner") > 0) {
    parts.push(`${String(n("with_the_owner"))} with the owner`);
  }
  if (waiting > 0) parts.push(`${String(waiting)} waiting`);
  if (parts.length === 0) {
    if (n("set_aside") > 0) {
      parts.push(`${String(n("set_aside"))} set aside`);
    }
    if (n("paid_by_another_company") > 0) {
      parts.push(
        `${String(n("paid_by_another_company"))} paid by another company`,
      );
    }
  }
  const more = countsComplete ? "" : " More may exist.";
  if (parts.length === 0) {
    if (countsComplete && hubIntakeCountsTotal(counts) === 0) {
      return "Nothing has come in from the hub yet this month.";
    }
    return `From the hub this month: nothing counted yet.${more}`;
  }
  return `From the hub this month: ${parts.join(", ")}.${more}`;
}
